/**
 * Ambiente fiscal.
 *
 * Fonte normativa: Plano Oficial, seção 6.3 — "Produção e homologação como
 * isolamento de primeira classe". Índices e chaves lógicas devem incluir o
 * ambiente; credenciais e endpoints são resolvidos por ambiente, sem fallback
 * implícito; nenhuma rotina pode converter uma linha de produção em homologação
 * ou vice-versa.
 *
 * O ambiente NUNCA tem valor padrão inferido. Toda operação fiscal declara o
 * seu ambiente explicitamente.
 */

export const FISCAL_ENVIRONMENTS = ['production', 'homologation'] as const;

export type FiscalEnvironment = (typeof FISCAL_ENVIRONMENTS)[number];

/**
 * Código numérico usado pelos autorizadores (tpAmb da NF-e: 1=produção,
 * 2=homologação). Mapeado explicitamente, nunca derivado por coerção.
 */
export const FISCAL_ENVIRONMENT_AUTHORIZER_CODE: Readonly<Record<FiscalEnvironment, '1' | '2'>> =
  Object.freeze({
    production: '1',
    homologation: '2',
  });

export function isFiscalEnvironment(value: unknown): value is FiscalEnvironment {
  return typeof value === 'string' && (FISCAL_ENVIRONMENTS as readonly string[]).includes(value);
}

/**
 * Resolve o ambiente a partir de uma entrada externa.
 *
 * Retorna `null` em vez de assumir produção ou homologação. Um ambiente
 * ausente ou desconhecido é erro de contrato, não um caso a ser adivinhado.
 */
export function parseFiscalEnvironment(value: unknown): FiscalEnvironment | null {
  return isFiscalEnvironment(value) ? value : null;
}

/** Rótulo para interface. A UI deve tornar o ambiente visível (Plano, 6.3). */
export const FISCAL_ENVIRONMENT_LABEL: Readonly<Record<FiscalEnvironment, string>> = Object.freeze({
  production: 'Produção',
  homologation: 'Homologação',
});
